import React, { useState, useRef } from 'react';
import { XMarkIcon, PencilSquareIcon, TrashIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import ConfirmationDialog from './ConfirmationDialog';
import FileAttachments from './FileAttachments';
import { useAuth } from '../../context/AuthContext';
import FileUploader from '../../components/FileUploader';

/**
 * EditableComment component for editing and deleting comments
 */
const EditableComment = ({ comment, shortName, onEditSuccess, onDeleteSuccess }) => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [content, setContent] = useState(comment.content || '');
  const [prevContent, setPrevContent] = useState(comment.content || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [files, setFiles] = useState(comment.files || []);
  const [filesToUpload, setFilesToUpload] = useState([]);
  
  // Initialize file uploader
  const {
    FileInput,
    AttachmentButton,
    FileList,
    dragProps,
    isDragging,
    fileInputRef
  } = FileUploader({
    files: filesToUpload,
    setFiles: setFilesToUpload,
    disabled: isUpdating
  });
  
  // Check if the current user can edit this comment
  // Compare email addresses since user object might be just the email string
  const canEditComment = user && comment.user && (
    user === comment.user || 
    (typeof user === 'object' && user.email === comment.user)
  );
  
  // Handle edit button click
  const handleEditClick = () => {
    if (!canEditComment) return;
    setPrevContent(content); // Store current content in case we need to revert
    setIsEditing(true);
  };
  
  // Handle field changes
  const handleChange = (e) => {
    setContent(e.target.value);
  };
  
  // Remove an existing file
  const handleRemoveFile = (fileToRemove) => {
    setFiles(prev => prev.filter(file => file.uuid !== fileToRemove.uuid));
  };
  
  // Handle saving the comment
  const handleSave = async () => {
    // If content hasn't changed and files haven't changed, just exit edit mode
    const originalFileCount = comment.files?.length || 0;
    const filesUnchanged = files.length === originalFileCount && 
      filesToUpload.length === 0 &&
      (files.length === 0 || files.every(file => 
        comment.files.some(origFile => origFile.uuid === file.uuid)));

    if (content === prevContent && filesUnchanged) {
      setIsEditing(false);
      return;
    }
    
    setIsUpdating(true);
    setError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeComment } = await import('../../utils/api');
      
      // Always replace files when files have changed
      const replaceFiles = true;
      
      // Call the API to update the comment
      const response = await updateScopeComment(
        shortName, 
        comment.id, 
        content, 
        filesToUpload.length > 0 ? filesToUpload : null,
        replaceFiles,
        files // Pass current files so API knows which existing files to keep
      );
      
      // Update the local files state with the response data if available
      if (response && response.files) {
        setFiles(response.files);
      }
      
      setIsEditing(false);
      setFilesToUpload([]);
      
      // Call the success callback if provided
      if (onEditSuccess && typeof onEditSuccess === 'function') {
        // No delay needed, we already have the updated files
        onEditSuccess();
      }
    } catch (error) {
      console.error('Error updating comment:', error);
      setError(`Error: ${error.message}`);
      // Don't revert to previous content here, let the user try again or cancel
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle canceling the edit
  const handleCancel = () => {
    setContent(prevContent); // Revert to previous content
    setIsEditing(false);
    setError(null);
    setFilesToUpload([]);
  };
  
  // Handle delete button click
  const handleDeleteClick = () => {
    if (!canEditComment) return;
    setShowDeleteConfirmation(true);
  };
  
  // Handle confirming comment deletion
  const handleConfirmDelete = async () => {
    setIsUpdating(true);
    setError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { deleteScopeComment } = await import('../../utils/api');
      
      // Call the API to delete the comment
      await deleteScopeComment(shortName, comment.id);
      
      setShowDeleteConfirmation(false);
      
      // Call the success callback if provided
      if (onDeleteSuccess && typeof onDeleteSuccess === 'function') {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      setError(`Error: ${error.message}`);
      setShowDeleteConfirmation(false);
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle canceling the delete confirmation
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };
  
  // Edit mode
  if (isEditing) {
    return (
      <div className="mt-2 text-sm text-gray-700">
        <div 
          className={`relative ${isDragging ? 'ring-2 ring-indigo-400' : ''}`}
          {...dragProps}
        >
          <textarea
            value={content}
            onChange={handleChange}
            className="block w-full rounded-md bg-white dark:bg-gray-800 px-3 py-1.5 text-base text-gray-900 dark:text-white outline-1 -outline-offset-1 outline-gray-300 dark:outline-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 min-h-[80px]"
            placeholder="Edit your comment..."
            disabled={isUpdating}
            autoFocus
            rows={3}
          />
          
          {/* File attachment area */}
          <div className="mt-3">
            {/* Hidden file input */}
            <FileInput />
            
            {/* Attachment button with text */}
            <div className="inline-flex">
              <AttachmentButton showText={true} />
            </div>
            
            {/* Show files queued for upload */}
            <div>
              <FileList />
            </div>
            
            {/* Show existing files that can be removed */}
            {files.length > 0 && (
              <div className="mt-2">
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Existing Attachments:
                </h4>
                <FileAttachments 
                  files={files}
                  onDeleteFile={handleRemoveFile}
                  isReadOnly={false}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          
          <div className="mt-3 flex justify-end space-x-2">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isUpdating}
              className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            
            <button
              type="button"
              onClick={handleSave}
              disabled={isUpdating || !content.trim()}
              className="rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:bg-gray-100 disabled:text-gray-400"
            >
              Save
            </button>
          </div>
          
          {isUpdating && (
            <div className="absolute right-2 top-4">
              <span className="text-xs text-gray-500 animate-pulse">Saving...</span>
            </div>
          )}
          
          {error && (
            <p className="mt-1 text-xs text-red-500">{error}</p>
          )}
        </div>
      </div>
    );
  }
  
  // View mode
  return (
    <div className="mt-2 text-sm text-gray-700 dark:text-gray-300 group">
      <p className="whitespace-pre-wrap">{content}</p>
      
      {/* Show file attachments if any */}
      {files && files.length > 0 && (
        <FileAttachments 
          files={files}
          isReadOnly={true}
          className="mt-2"
        />
      )}
      
      {/* Always render a div of the same height to maintain consistent spacing */}
      <div className="h-5 mt-1.5 mb-0">
        {/* Only show edit/delete buttons for comments the current user can edit */}
        {canEditComment && (
          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={handleEditClick}
              className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            >
              <PencilSquareIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Edit
            </button>
            <button
              type="button"
              onClick={handleDeleteClick}
              className="inline-flex items-center text-xs text-gray-500 hover:text-red-600 dark:hover:text-red-400"
            >
              <TrashIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Delete
            </button>
          </div>
        )}
      </div>
      
      {/* Delete confirmation dialog */}
      <ConfirmationDialog
        open={showDeleteConfirmation}
        onClose={handleCancelDelete}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
        confirmButtonText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      )}
    </div>
  );
};

export default EditableComment;